import {ManageClientService} from '@store/clients/services';
import ModalComponent from '@components/modal/Modal.component';
import {useTranslation} from 'react-i18next';
import {Button, Stack, Typography} from '@mui/material';
import dayjs from '@utils/dayjs.utils';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import React, {useState} from 'react';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import {LoadingButton} from '@mui/lab';
import {executionLineService} from '@store/invoices/executionLines';
import {useSnackbar} from 'notistack';

interface FixPricesModalProps {
  handleClose: () => void;
  service: ManageClientService;
}

const FixPricesModal = (props: FixPricesModalProps) => {
  const {handleClose, service} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [sellPrice, setSellPrice] = useState<number | undefined>(undefined);
  const [buyPrice, setBuyPrice] = useState<number | undefined>(undefined);

  const [loading, setLoading] = useState(false);

  const errors = {
    from: getFieldError(from, FieldValidationType.REQUIRED_DATE),
    to: getFieldError(to, FieldValidationType.REQUIRED_DATE),
    price: getFieldError(sellPrice, FieldValidationType.REQUIRED_NUMBER) && getFieldError(buyPrice, FieldValidationType.REQUIRED_NUMBER)
  };

  const handleSave = () => {
    setLoading(true);
    if (service.id && from && to) {
      executionLineService.fixPrices(service.id, {from, to, sellPrice, buyPrice})
        .subscribe({
          next: (res) => {
            enqueueSnackbar(t('clients.details.contractedServices.manage.fixPrices.updatedCount', {count: res.updatedCount}), {variant: 'success'});
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),

        });
    }

  };

  return <ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    title={t('clients.details.contractedServices.manage.fixPrices.title')}
    content={(
      <Stack direction="column" width="70%" spacing={2} pt="55px" pb="10px">
        <Typography fontWeight="bold">
          {t('invoices.bills.create.title')}
        </Typography>
        <DateRangePickerComponent
          label={t('clients.details.contractedServices.manage.fixPrices.fromTo')}
          startDate={from}
          color="secondary"
          endDate={to}
          handleChangeStartDate={(from) => setFrom(from ? dayjs(from).startOf('day').toDate() : undefined)}
          handleChangeEndDate={(to) => setTo(to ? dayjs(to).endOf('day').toDate() : undefined)}
          error={errors.from || errors.to}/>
        <Typography fontWeight="bold">
          {t('clients.details.contractedServices.manage.fixPrices.sellPrice')}
        </Typography>
        <NumberTextFieldComponent
          color="secondary"
          placeholder={t('global.writeSomething')}
          pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
          handleChange={(unitCost) => setSellPrice(Math.round((unitCost || 0) * 100) / 100)}
          value={sellPrice}
          error={errors.price}
          required/>
        <Typography fontWeight="bold">
          {t('clients.details.contractedServices.manage.fixPrices.buyPrice')}
        </Typography>
        <NumberTextFieldComponent
          color="secondary"
          placeholder={t('global.writeSomething')}
          pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
          handleChange={(unitCost) => setBuyPrice(Math.round((unitCost || 0) * 100) / 100)}
          value={buyPrice}
          error={errors.price}
          required/>
      </Stack>
    )}
    actions={(
      <>
        <Button
          variant="outlined"
          onClick={handleClose}
          color="primary">
          {t('global.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          disabled={checkIfErrors(errors)}
          loading={loading}
          onClick={handleSave}
          color="primary">
          {t('global.save')}
        </LoadingButton>
      </>
    )}
  />;
};

export default FixPricesModal;
